import { useEffect, useMemo, useState, useCallback } from 'react';
import { Mail, Plus, Trash2, Edit, Loader2, DatabaseZap, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  DataTable,
  Pagination,
  SearchInput,
  useAuth,
  useLocale,
  toast,
} from '@aaas/shared';
import { useUrlPagination } from '../../hooks/useUrlPagination';
import type { DataTableColumn, DataTableAction } from '@aaas/shared';
import { useEmailTemplateListQuery } from '../../hooks/useEmailQueries';
import emailService from '../../services/email.service';
import { EmailTemplateFormDialog } from '../../components/EmailTemplateFormDialog';
import { sanitizeHtml } from '../../utils/sanitize-html';
import {
  DEFAULT_EMAIL_TEMPLATES_BY_LOCALE,
  DEFAULT_EMAIL_TEMPLATES_FALLBACK,
} from '../../data/default-email-templates';
import type { EmailTemplate } from '../../types';

export function EmailTemplateConfigPage() {
  const { user, idToken, currentWorkspace } = useAuth();
  const { t } = useTranslation('ats');
  const { locale } = useLocale();
  const defaultEmailTemplates = useMemo(
    () =>
      DEFAULT_EMAIL_TEMPLATES_BY_LOCALE[locale] ??
      DEFAULT_EMAIL_TEMPLATES_FALLBACK,
    [locale],
  );
  const { page, size, filters, setPage, setSize, setFilters } =
    useUrlPagination({ filterKeys: ['search'] });
  const searchValue = filters.search as string | undefined;

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [showDefaultDialog, setShowDefaultDialog] = useState(false);
  const [isCreatingDefaults, setIsCreatingDefaults] = useState(false);
  const [selectedDefaults, setSelectedDefaults] = useState<Set<string>>(
    () => new Set(defaultEmailTemplates.map((tpl) => tpl.name)),
  );

  useEffect(() => {
    setSelectedDefaults(new Set(defaultEmailTemplates.map((tpl) => tpl.name)));
  }, [defaultEmailTemplates]);

  const {
    data: templateResponse,
    isLoading,
    isFetching,
    refetch,
  } = useEmailTemplateListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page,
    size,
    search: searchValue,
    enabled: !!user && !!currentWorkspace,
    refetchInterval: 5000,
  });

  const templates = templateResponse?.data?.items || [];
  const totalItems = templateResponse?.data?.total || 0;
  const totalPages = Math.ceil(totalItems / size);

  const handleCreateClick = useCallback(() => {
    setFormMode('create');
    setEditingTemplate(null);
    setShowFormDialog(true);
  }, []);

  const handleEditClick = useCallback((template: EmailTemplate) => {
    if (template.is_system) {
      toast.error(t('emailTemplate.cannotEditSystem'));
      return;
    }
    setFormMode('edit');
    setEditingTemplate(template);
    setShowFormDialog(true);
  }, [t]);

  const handleDeleteClick = useCallback((template: EmailTemplate) => {
    if (template.is_system) {
      toast.error(t('emailTemplate.cannotDeleteSystem'));
      return;
    }
    setSelectedTemplate(template);
    setDeleteError(null);
    setShowDeleteDialog(true);
  }, [t]);

  const handleConfirmDelete = useCallback(async () => {
    if (!user || !currentWorkspace || !selectedTemplate) return;

    setIsDeleting(true);
    setDeleteError(null);
    setDeletingIds((prev) => new Set(prev).add(selectedTemplate.id));

    try {
      const token = await user.getIdToken();
      await emailService.deleteTemplate(selectedTemplate.id, currentWorkspace.id, token);
      toast.success(t('emailTemplate.deleted'));
      setShowDeleteDialog(false);
      setSelectedTemplate(null);
      refetch();
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('emailTemplate.deleteFailed');
      setDeleteError(msg);
    } finally {
      setIsDeleting(false);
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        if (selectedTemplate) newSet.delete(selectedTemplate.id);
        return newSet;
      });
    }
  }, [user, currentWorkspace, selectedTemplate, refetch]);

  const handleFormSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleOpenDefaultsDialog = useCallback(() => {
    setSelectedDefaults(new Set(defaultEmailTemplates.map((tpl) => tpl.name)));
    setShowDefaultDialog(true);
  }, [defaultEmailTemplates]);

  const handleSeedDefaults = useCallback(async () => {
    if (!user || !currentWorkspace || selectedDefaults.size === 0) return;
    setIsCreatingDefaults(true);
    try {
      const token = await user.getIdToken();
      const templatesPayload = defaultEmailTemplates
        .filter((tpl) => selectedDefaults.has(tpl.name))
        .map((tpl) => ({
          name: tpl.name,
          category: tpl.category,
          subject: tpl.subject,
          body_html: tpl.body_html,
        }));
      const result = await emailService.bulkCreateTemplates(
        currentWorkspace.id,
        token,
        templatesPayload,
      );
      if (result.created > 0) {
        toast.success(
          result.skipped > 0
            ? t('emailTemplate.createdWithSkipped', {
                count: result.created,
                skipped: result.skipped,
              })
            : t('emailTemplate.createdCount', { count: result.created }),
        );
      } else {
        toast.info(t('emailTemplate.alreadyExist'));
      }
      setShowDefaultDialog(false);
      refetch();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('emailTemplate.createDefaultsFailed'),
      );
    } finally {
      setIsCreatingDefaults(false);
    }
  }, [user, currentWorkspace, defaultEmailTemplates, refetch, selectedDefaults, t]);

  const handleDialogClose = () => {
    if (!isDeleting) {
      setShowDeleteDialog(false);
      setSelectedTemplate(null);
      setDeleteError(null);
    }
  };

  const columns: DataTableColumn<EmailTemplate>[] = [
    {
      key: 'name',
      title: t('emailTemplate.columnName'),
      render: (_value, record) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{record.name}</span>
          {record.is_system && (
            <span className="inline-flex items-center text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
              {t('emailTemplate.systemBadge')}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      title: t('emailTemplate.columnCategory'),
      width: 160,
      render: (_value, record) =>
        record.category ? (
          <span className="inline-flex items-center text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
            {record.category}
          </span>
        ) : null,
    },
    {
      key: 'subject',
      title: t('emailTemplate.columnSubject'),
      render: (_value, record) => (
        <span className="text-sm text-gray-600 truncate max-w-xs block">
          {record.subject}
        </span>
      ),
    },
    {
      key: 'created_at',
      title: t('config.columnCreated'),
      width: 120,
      render: (_value, record) => (
        <span className="text-sm text-gray-500">
          {new Date(record.created_at).toLocaleDateString('vi-VN')}
        </span>
      ),
    },
  ];

  const actions: DataTableAction<EmailTemplate>[] = [
    {
      key: 'view',
      label: t('emailTemplate.view'),
      icon: <Eye className="h-4 w-4" />,
      onClick: (record) => setPreviewTemplate(record),
      inline: true,
    },
    {
      key: 'edit',
      label: t('config.edit'),
      icon: <Edit className="h-4 w-4" />,
      onClick: handleEditClick,
      disabled: (row) => row.is_system,
      inline: true,
    },
    {
      key: 'delete',
      label: t('config.delete'),
      icon: <Trash2 className="h-4 w-4" />,
      loadingIcon: <Loader2 className="h-4 w-4 animate-spin" />,
      onClick: handleDeleteClick,
      loading: (record) => deletingIds.has(record.id),
      destructive: true,
      disabled: (row) => row.is_system,
      inline: true,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Mail className="h-8 w-8 text-orange-600" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{t('emailTemplate.pageTitle')}</h1>
              {isFetching && !isLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              )}
            </div>
            <p className="text-sm text-gray-500">{t('emailTemplate.pageDescription')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleOpenDefaultsDialog}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <DatabaseZap className="h-4 w-4" />
            {t('config.createDefaults')}
          </button>
          <button
            onClick={handleCreateClick}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t('emailTemplate.newOne')}
          </button>
        </div>
      </div>

      {/* Search */}
      <SearchInput
        placeholder={t('emailTemplate.searchPlaceholder')}
        onSearch={(value: string) => {
          setFilters({ search: value || undefined });
          setPage(1);
        }}
        initialValue={searchValue || ''}
      />

      {/* Table */}
      <div className="space-y-4">
        <DataTable
          data={templates}
          columns={columns}
          actions={actions}
          loading={isLoading}
          emptyMessage={t('emailTemplate.empty')}
          rowKey={(record) => record.id}
        />

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          pageSize={size}
          onPageSizeChange={setSize}
          totalItems={totalItems}
          showTotal={true}
          showPageSizeSelector={true}
        />
      </div>

      {/* Create/Edit Dialog */}
      <EmailTemplateFormDialog
        key={`${formMode}-${editingTemplate?.id ?? 'new'}-${showFormDialog}`}
        open={showFormDialog}
        onClose={() => setShowFormDialog(false)}
        mode={formMode}
        template={editingTemplate}
        onSuccess={handleFormSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={handleDialogClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('emailTemplate.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('emailTemplate.deleteBody')}</AlertDialogDescription>
            {deleteError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <span className="text-sm text-red-600">{deleteError}</span>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('config.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.deleting')}
                </>
              ) : (
                t('config.delete')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Defaults Dialog */}
      <AlertDialog
        open={showDefaultDialog}
        onOpenChange={(open) => !isCreatingDefaults && setShowDefaultDialog(open)}
      >
        <AlertDialogContent className="sm:max-w-[600px]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('emailTemplate.defaultsTitle')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>{t('emailTemplate.defaultsDescription')}</p>
                <div className="mt-3 max-h-80 overflow-y-auto space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={
                        defaultEmailTemplates.length > 0 &&
                        selectedDefaults.size === defaultEmailTemplates.length
                      }
                      onCheckedChange={(checked) => {
                        setSelectedDefaults(
                          checked
                            ? new Set(defaultEmailTemplates.map((tpl) => tpl.name))
                            : new Set(),
                        );
                      }}
                    />
                    <span className="text-sm font-medium">{t('config.createDefaultsSelectAll')}</span>
                  </label>
                  <hr className="my-1" />
                  {defaultEmailTemplates.map((tpl) => (
                    <label
                      key={tpl.name}
                      className="flex items-start gap-2 cursor-pointer py-1"
                    >
                      <Checkbox
                        className="mt-1"
                        checked={selectedDefaults.has(tpl.name)}
                        onCheckedChange={(checked) => {
                          setSelectedDefaults((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(tpl.name);
                            else next.delete(tpl.name);
                            return next;
                          });
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{tpl.name}</span>
                          {tpl.category && (
                            <span className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">
                              {tpl.category}
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="mt-3 text-sm text-gray-500">{t('emailTemplate.defaultsSkipNote')}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCreatingDefaults}>{t('config.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleSeedDefaults();
              }}
              disabled={isCreatingDefaults || selectedDefaults.size === 0}
            >
              {isCreatingDefaults ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.creating')}
                </>
              ) : selectedDefaults.size > 0 ? (
                t('emailTemplate.createN', { count: selectedDefaults.size })
              ) : (
                t('config.confirm')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={(open) => !open && setPreviewTemplate(null)}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewTemplate?.name}
              {previewTemplate?.category && (
                <span className="inline-flex items-center text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-normal">
                  {previewTemplate.category}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {previewTemplate && (
            <div className="space-y-4">
              <div className="text-sm">
                <span className="text-gray-500">Subject: </span>
                <span className="font-medium">{previewTemplate.subject}</span>
              </div>
              <div className="border rounded-md p-4 bg-white">
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewTemplate.body_html) }}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
